import { type Ref, ref } from 'vue'

import {
  BRAND_SEARCH_QUERY_MAX_LENGTH,
  searchBrandCandidates as repoSearchBrandCandidates,
} from '@/repositories/competition'
import type { BrandCandidate } from '@/types/competition'

/** Why a company search came back without results to show. */
export type BrandSearchError = 'query_too_long' | 'search_failed'

/** A company search the founder submits: the typed text, the results and where the search stands. */
export interface BrandSearch {
  /** The typed text. */
  readonly query: Ref<string>
  /** The companies the latest search found. */
  readonly results: Ref<readonly BrandCandidate[]>
  /** True while a search is in flight. */
  readonly searching: Ref<boolean>
  /** Why the latest search showed no results, or `null`. */
  readonly searchError: Ref<BrandSearchError | null>
  /** The text the shown results were found for, or `null` before any search. */
  readonly searchedQuery: Ref<string | null>
  /** Searches the typed text. Runs only when the founder submits it. */
  search(): Promise<void>
  /** Forgets the typed text and the results. */
  clearSearch(): void
}

/** A company search by name or website. A newer search always wins over an older one. */
export function useBrandSearch(): BrandSearch {
  const query = ref('')
  const results = ref<readonly BrandCandidate[]>([])
  const searching = ref(false)
  const searchError = ref<BrandSearchError | null>(null)
  const searchedQuery = ref<string | null>(null)
  let sequence = 0

  function clearSearch(): void {
    sequence += 1
    query.value = ''
    results.value = []
    searching.value = false
    searchError.value = null
    searchedQuery.value = null
  }

  async function search(): Promise<void> {
    const text = query.value.trim()
    if (text === '') return
    sequence += 1
    const current = sequence
    if (text.length > BRAND_SEARCH_QUERY_MAX_LENGTH) {
      results.value = []
      searching.value = false
      searchError.value = 'query_too_long'
      return
    }
    searching.value = true
    searchError.value = null
    try {
      const found = await repoSearchBrandCandidates(text)
      if (current !== sequence) return
      results.value = found
      searchedQuery.value = text
    } catch {
      if (current !== sequence) return
      results.value = []
      searchError.value = 'search_failed'
    } finally {
      if (current === sequence) searching.value = false
    }
  }

  return { query, results, searching, searchError, searchedQuery, search, clearSearch }
}
